import React from 'react';
import { StaticPageLayout } from '@/components/StaticPageLayout';
import {
  Calendar,
  ExternalLink,
  Users,
  FileText,
  Award,
  Heart,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { Link } from '@inertiajs/react';
import { format } from 'date-fns';

type Event = {
  id: number;
  title: string;
  description: string;
  event_date: string;
  registration_url: string;
};

type Document = {
  id: number;
  key: string;
  title: string;
  document_type: string;
};

type Section = {
  key: string;
  title: string;
  description: string;
  link_text: string;
  link_url: string;
  documents?: Document[];
};

type Props = {
  events: Event[];
  sections: Section[];
  available_documents: Record<string, string>;
};

const sectionIcons: Record<string, React.ReactNode> = {
  for_mentees: <Users className="h-5 w-5 text-primary" />,
  for_pregrad_mentors: <FileText className="h-5 w-5 text-primary" />,
  for_prehealth_mentors: <Heart className="h-5 w-5 text-primary" />,
  for_pregrad_mentees: <Award className="h-5 w-5 text-primary" />,
  fee_waivers: <Award className="h-5 w-5 text-primary" />,
};

export default function CommunityIndex({
  events,
  sections,
  available_documents,
}: Props) {
  return (
    <StaticPageLayout>
      {/* Hero Section - Clean and Direct */}
      <section className="bg-gradient-to-b from-white to-gray-50/50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Community Hub
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Access resources, join events, and connect with your mentorship
              community.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="mailto:contact@project-short.com"
                className="inline-flex items-center px-5 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors"
              >
                Contact Support
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Ongoing Events - Modern Cards */}
      {events.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900">
                Upcoming Events
              </h2>
              <p className="text-gray-600 mt-2">
                Join our community sessions and workshops
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="bg-white border-0 rounded-xl shadow-sm hover:shadow-md transition-all p-6"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Calendar className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-gray-600">
                      {format(new Date(event.event_date), 'MMM d, yyyy')}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {event.title}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {event.description}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-500 flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {format(new Date(event.event_date), 'h:mm a')}
                    </span>
                    {event.registration_url && (
                      <a
                        href={event.registration_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-primary hover:text-primary/80 flex items-center"
                      >
                        Register
                        <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Resource Sections - Clean Grid */}
      {sections.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900">
                Resources & Documents
              </h2>
              <p className="text-gray-600 mt-2">
                Essential materials for mentors and mentees
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {sections.map((section) => (
                <div
                  key={section.key}
                  className="group bg-white rounded-xl border border-gray-100 p-6 hover:shadow-lg hover:border-gray-200 transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2.5 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg group-hover:from-primary/15 group-hover:to-primary/10 transition-colors">
                      {sectionIcons[section.key] || (
                        <FileText className="h-5 w-5 text-primary" />
                      )}
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    {section.title}
                  </h3>
                  {section.description && (
                    <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                      {section.description}
                    </p>
                  )}
                  <div className="space-y-2">
                    {/* Display documents - we know all sections have documents now */}
                    {(section.documents || []).map((doc) => (
                      <Link
                        key={doc.id}
                        href={`/community/terms/${doc.key}`}
                        className="flex items-center text-sm text-primary hover:text-primary/80 font-medium group/link"
                      >
                        {doc.title}
                        <ArrowRight className="ml-1 h-3 w-3 group-hover/link:translate-x-0.5 transition-transform" />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </StaticPageLayout>
  );
}
